<script>
  import { onMount } from 'svelte';
  import { api } from '../lib/api.js';

  let data = null;
  let loading = true;
  let error = null;

  onMount(() => { load(false); loadKw(false); loadQ(false); });

  // ---- Paid-search keyword history (Firestore, written by the ads collector) ----
  let kw = null, kwLoading = true, kwError = null;
  let showInactive = false;
  let sortKey = 'spend', sortDir = -1;
  const dayStr = (offset) => { const d = new Date(); d.setUTCDate(d.getUTCDate() - offset); return d.toISOString().slice(0, 10); };
  let rangeFrom = dayStr(28), rangeTo = dayStr(1), dateBy = 'order';
  // Which ad engine the keyword section reads: spend and orders always come from the same engine(s).
  let source = 'all';
  const ENGINE = { bing: 'Bing', google: 'Google', all: 'Bing + Google' };
  const STORE_SINCE = '2026-08-30';
  const basePresets = [
    { label: '7 days', from: dayStr(7) }, { label: '28 days', from: dayStr(28) }, { label: '90 days', from: dayStr(90) },
    { label: 'since full attribution (30 Aug)', from: STORE_SINCE }, { label: 'all', from: '2026-06-01' },
  ];
  // Milestones (manual annotations) become "since …" presets too.
  $: presets = [...basePresets, ...((kw && kw.milestones) || []).map((m) => ({ label: `since ${m.detail.length > 22 ? m.detail.slice(0, 21) + '…' : m.detail} (${fmtDay(m.date)})`, from: m.date, milestone: true }))];
  let msDate = dayStr(0), msLabel = '', msBusy = false, msError = null;
  async function addMilestone() {
    if (!msLabel.trim()) return;
    msBusy = true; msError = null;
    try { await api('/analytics/milestones', { method: 'POST', body: JSON.stringify({ date: msDate, label: msLabel.trim() }) }); msLabel = ''; await loadKw(true); }
    catch (e) { msError = e.message; } finally { msBusy = false; }
  }
  async function removeMilestone(m) {
    if (!confirm(`Remove milestone "${m.detail}" (${m.date})?`)) return;
    try { await api(`/analytics/milestones/${m.id}`, { method: 'DELETE' }); await loadKw(true); } catch (e) { msError = e.message; }
  }
  function setRange(from, to) { rangeFrom = from; rangeTo = to || dayStr(1); loadKw(false); }
  // Keyword links carry the current range so the detail page opens on the same window.
  $: kwLink = `?from=${rangeFrom}&to=${rangeTo}`;
  // Each load takes a sequence number and only the newest one may write back: switching engine or range
  // again while a (3–4 s uncached) load is in flight used to let the older response land last and
  // overwrite the newer one, so the stats stopped matching the selector.
  let kwSeq = 0;
  async function loadKw(refresh) {
    const seq = ++kwSeq;
    kwLoading = true; kwError = null;
    try { const r = await api(`/analytics/keywords?from=${rangeFrom}&to=${rangeTo}&dateBy=${dateBy}&source=${source}${refresh ? '&refresh=1' : ''}`); if (seq === kwSeq) kw = r; }
    catch (e) { if (seq === kwSeq) kwError = e.message; } finally { if (seq === kwSeq) kwLoading = false; }
    loadQ(refresh);
  }
  // ---- Search queries (query-first view) + planner coverage ----
  let qd = null, qLoading = true, qError = null;
  let qFilter = 'all', qSearch = '', qSort = 'clicks', qDir = -1, qLimit = 60;
  let covMin = 100, covStatus = 'unseen', covIntent = true, covSort = 'avg', covLimit = 40;
  let qSeq = 0;
  async function loadQ(refresh) {
    const seq = ++qSeq;
    qLoading = true; qError = null;
    try { const r = await api(`/analytics/queries?from=${rangeFrom}&to=${rangeTo}${refresh ? '&refresh=1' : ''}`); if (seq === qSeq) qd = r; }
    catch (e) { if (seq === qSeq) qError = e.message; } finally { if (seq === qSeq) qLoading = false; }
  }
  const qVal = (r, k) => k === 'query' ? r.query : k === 'served' ? r.via.length : r[k];
  function qSortBy(k) { if (qSort === k) qDir = -qDir; else { qSort = k; qDir = k === 'query' ? 1 : -1; } }
  $: qRows = qd ? qd.queries
      .filter((r) => qFilter === 'all' ? true : qFilter === 'nokw' ? !r.ownKeywordId : qFilter === 'multi' ? r.multi : qFilter === 'new' ? r.newInRange : qFilter === 'orders' ? r.storeOrders > 0 : r.suggestion === qFilter)
      .filter((r) => !qSearch || r.query.includes(qSearch.toLowerCase()))
      .slice().sort((a, b) => { const va = qVal(a, qSort), vb = qVal(b, qSort); if (va == null && vb == null) return 0; if (va == null) return 1; if (vb == null) return -1; return (va > vb ? 1 : va < vb ? -1 : 0) * qDir; }) : [];
  $: covRows = qd ? qd.coverage
      .filter((c) => c.avgMonthly >= covMin && (covStatus === 'all' || c.status === covStatus) && (!covIntent || (c.ecardIntent && !c.offTheme)))
      .slice().sort((a, b) => covSort === 'peak' ? b.peakSearches - a.peakSearches : covSort === 'season' ? (b.seasonality || 0) - (a.seasonality || 0) : b.avgMonthly - a.avgMonthly) : [];
  const sugLabel = { keyword: 'keyword', promote: 'promote', negative: 'negative?', review: 'review', ok: '' };
  const covLabel = { keyword: 'our keyword', bought: 'bought', seen: 'seen, no clicks', unseen: 'never seen' };
  const fmtDay = (s) => (s ? new Date(s + 'T00:00:00Z').toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : '—');
  const kwVal = (k, key) => {
    switch (key) {
      case 'text': return k.text;
      case 'bid': return k.bid;
      case 'firstPage': return k.latest.firstPageBid;
      case 'mainline': return k.latest.mainlineBid;
      case 'qs': return k.latest.qualityScore;
      case 'is': return k.latest.impressionShare;
      case 'clicks': return k.r.clicks;
      case 'cpc': return k.r.cpc;
      case 'orders': return k.r.storeOrders;
      case 'cvr': return k.r.cvr;
      case 'cpo': return k.r.cpo;
      case 'revenue': return k.r.revenue;
      case 'roas': return k.r.roas;
      case 'net': return k.r.net;
      case 'bing': return k.r.engineConversions;
      case 'comp': return k.competitorCount;
      default: return k.r.spend;
    }
  };
  function sortBy(key) { if (sortKey === key) sortDir = -sortDir; else { sortKey = key; sortDir = key === 'text' ? 1 : -1; } }
  $: kwRows = kw ? kw.keywords
      .filter((k) => showInactive ? true : (k.r.impressions > 0 || (k.present && k.status === 'Active' && k.adGroupStatus === 'Active' && k.campaignStatus === 'Active')))
      .slice().sort((a, b) => { const va = kwVal(a, sortKey), vb = kwVal(b, sortKey); if (va == null && vb == null) return 0; if (va == null) return 1; if (vb == null) return -1; return (va > vb ? 1 : va < vb ? -1 : 0) * sortDir; }) : [];
  const money = (v) => (v == null ? '—' : `£${Number(v).toFixed(2)}`);
  const pc = (v) => (v == null ? '—' : `${Math.round(v)}%`);
  const delta = (v) => (v == null ? '' : v > 0.05 ? ` ▲${Math.round(v * 100)}%` : v < -0.05 ? ` ▼${Math.round(-v * 100)}%` : '');
  const sevColor = { good: '#1c7a2e', warn: '#b26a00', error: '#b00020', info: '#2a5db0' };

  // ---- daily spend / orders / trailing CPO chart (inline SVG) ----
  const CW = 960, CH = 220, CP = { l: 44, r: 44, t: 14, b: 26 };
  $: daily = kw ? kw.daily : [];
  $: n = daily.length;
  $: slot = n ? (CW - CP.l - CP.r) / n : 0;
  $: cx = (i) => CP.l + slot * (i + 0.5);
  $: spendMax = Math.max(1, ...daily.map((d) => d.spend)) * 1.35; // headroom for order counts + milestone labels
  $: ySpend = (v) => CP.t + (CH - CP.t - CP.b) * (1 - v / spendMax);
  $: cpoMax = Math.max(1, ...daily.map((d) => d.cpo7 || 0), ...daily.map((d) => Math.min(d.cpo || 0, 40)));
  $: yCpo = (v) => CP.t + (CH - CP.t - CP.b) * (1 - v / cpoMax);
  $: cpoPath = daily.reduce((acc, d, i) => (d.cpo7 == null ? acc : acc + (acc && daily[i - 1] && daily[i - 1].cpo7 != null ? 'L' : 'M') + `${cx(i).toFixed(1)},${yCpo(d.cpo7).toFixed(1)}`), '');
  $: dTicks = daily.filter((_, i) => i % Math.max(1, Math.round(n / 8)) === 0 || i === n - 1);
  $: msMarksList = kw ? (kw.milestones || []).map((m) => { const i = daily.findIndex((d) => d.date >= m.date); return i < 0 || m.date > (daily[n - 1] || {}).date ? null : { ...m, x: cx(i) - slot / 2 }; }).filter(Boolean) : [];
  const tip = (d) => `${fmtDay(d.date)}: £${d.spend.toFixed(2)} spend · ${d.clicks} clicks · ${d.orders} order${d.orders === 1 ? '' : 's'} · £${d.revenue.toFixed(2)} revenue · CPO ${d.cpo == null ? '—' : '£' + d.cpo.toFixed(2)} · 7-day CPO ${d.cpo7 == null ? '—' : '£' + d.cpo7.toFixed(2)} · 7-day ROAS ${d.roas7 ?? '—'}`;

  async function load(refresh) {
    loading = true;
    error = null;
    try {
      data = await api(`/analytics${refresh ? '?refresh=1' : ''}`);
    } catch (e) {
      error = e.message;
    } finally {
      loading = false;
    }
  }

  $: fmtMoney = (v) => {
    if (v === null || v === undefined) return '—';
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: (data && data.currency) || 'GBP',
    }).format(v);
  };
  const fmtPct = (v) => (v === null || v === undefined ? '—' : `${(v * 100).toFixed(1)}%`);
  const fmtDate = (s) => {
    if (!s) return '—';
    try { return new Date(s).toLocaleDateString('en-GB', { dateStyle: 'medium' }); }
    catch (e) { return s; }
  };
  const fmtTime = (s) => {
    if (!s) return '—';
    try { return new Date(s).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' }); }
    catch (e) { return s; }
  };
  const periodLabel = (p) => (p.days ? `Last ${p.days} days` : 'Full history');

  // Period rows the headline cards read from.
  $: p30 = data && data.periods.find((p) => p.days === 30);
  $: maxDay = data ? Math.max(1, ...data.ordersByDay.map((d) => d.orders)) : 1;
</script>

<div class="head">
  <h2>Analytics</h2>
  {#if data}
    <span class="meta">
      computed {fmtTime(data.computedAt)}{data.cached ? ' (cached)' : ''}
    </span>
    <button class="refresh" on:click={() => load(true)} disabled={loading}>Refresh</button>
  {/if}
</div>

{#if loading && !data}
  <p>Loading analytics… (first load pages through the full order history)</p>
{:else if error}
  <p class="error">{error}</p>
{:else if data}
  {#if data.ordersWindow.limited}
    <p class="note">
      ⚠️ Order-level stats only cover orders since <b>{fmtDate(data.ordersWindow.from)}</b> —
      Shopify limits apps to 60 days of order history without the <code>read_all_orders</code>
      scope. Customer and recipient figures below are lifetime and unaffected.
    </p>
  {/if}

  <div class="cards">
    <div class="card">
      <span class="label">Average order value <small>(30 days)</small></span>
      <span class="value">{fmtMoney(p30 && p30.aov)}</span>
    </div>
    <div class="card">
      <span class="label">Orders per day <small>(30 days)</small></span>
      <span class="value">{p30 ? p30.perDay : '—'}</span>
    </div>
    <div class="card">
      <span class="label">Returning customers <small>(lifetime)</small></span>
      <span class="value">{fmtPct(data.customers.returningRate)}</span>
      <span class="sub">{data.customers.returning} of {data.customers.total} buyers</span>
    </div>
    <div class="card">
      <span class="label">Recipients → customers</span>
      <span class="value">{data.recipients.convertedAfter}</span>
      <span class="sub">{data.recipients.matched} matched of {data.recipients.emailRecipients} scanned</span>
    </div>
  </div>

  <h3>Orders by period</h3>
  <table>
    <thead>
      <tr><th>Period</th><th>Orders</th><th>Revenue</th><th>AOV</th><th>Orders/day</th><th>From returning customers</th></tr>
    </thead>
    <tbody>
      {#each data.periods as p}
        <tr>
          <td>{periodLabel(p)}</td>
          <td>{p.orders}</td>
          <td>{fmtMoney(p.revenue)}</td>
          <td>{fmtMoney(p.aov)}</td>
          <td>{p.perDay}</td>
          <td>{p.repeatOrders} ({fmtPct(p.repeatShare)})</td>
        </tr>
      {/each}
    </tbody>
  </table>
  <p class="sub-note">
    Lifetime (customers API): {data.customers.lifetimeOrders} orders,
    {fmtMoney(data.customers.lifetimeRevenue)} from {data.customers.total} buyers.
  </p>

  <h3>Orders per day <small>(last 30 days)</small></h3>
  <div class="chart">
    {#each data.ordersByDay as d}
      <div class="bar-slot" title={`${fmtDate(d.date)}: ${d.orders} order${d.orders === 1 ? '' : 's'}`}>
        <div class="bar" style={`height:${Math.round((d.orders / maxDay) * 100)}%`}></div>
      </div>
    {/each}
  </div>

  <h3>Ecard recipients who became customers</h3>
  <p class="sub-note">
    Scanned {data.recipients.emailRecipients} distinct recipient emails across
    {data.ecardCount} ecards (self-sends excluded; {data.recipients.unscannable} SMS/link
    deliveries have no email to match). {data.recipients.matched} also appear as Shopify
    customers; {data.recipients.convertedAfter} first purchased <em>after</em> receiving an ecard
    ({fmtPct(data.recipients.conversionRate)} of scannable recipients).
  </p>
  {#if data.recipients.matches.length === 0}
    <p class="empty">No recipient emails match a Shopify customer yet.</p>
  {:else}
    <table>
      <thead>
        <tr><th>Recipient</th><th>Email</th><th>Ecards received</th><th>First received</th><th>Customer since</th><th>Orders</th><th>Spent</th><th></th></tr>
      </thead>
      <tbody>
        {#each data.recipients.matches as m}
          <tr>
            <td>{m.name || '—'}</td>
            <td>{m.email}</td>
            <td>{m.ecardsReceived}</td>
            <td>{fmtDate(m.firstReceivedAt)}</td>
            <td>{fmtDate(m.customerSince)}</td>
            <td>{m.orders}</td>
            <td>{fmtMoney(m.totalSpent)}</td>
            <td>
              {#if m.convertedAfter}
                <span class="badge ok">converted after ecard</span>
              {:else}
                <span class="badge info">already a customer</span>
              {/if}
            </td>
          </tr>
        {/each}
      </tbody>
    </table>
  {/if}
{/if}

<h3 id="keywords">Paid search keywords <small>{ENGINE[source]} · store-side conversions</small>{#if kwLoading && kw}<span class="updating">updating…</span>{/if}</h3>
{#if kwLoading && !kw}
  <p>Loading keyword history…</p>
{:else if kwError}
  <p class="error">{kwError}</p>
{:else if kw}
  {#if kw.lastRun && !kw.lastRun.ok}<p class="note">⚠️ Last collector run ({fmtTime(kw.lastRun.at)}) failed: {kw.lastRun.failed.join(' · ')}</p>{/if}
  {#each kw.errors as e}<p class="note">⚠️ collector error on {e.date} ({e.job}): {e.detail}</p>{/each}
  {#if source !== 'bing' && !kw.googleExport}<p class="note">Google spend isn't connected yet: the Google Ads export script hasn't been read by the collector. Google orders below are real; spend, clicks and CPO show only Bing (or nothing) until it is.</p>{/if}
  {@const t = kw.totals}
  <div class="rangebar">
    <label>From <input type="date" bind:value={rangeFrom} min="2026-06-01" max={rangeTo} on:change={() => loadKw(false)} /></label>
    <label>To <input type="date" bind:value={rangeTo} min={rangeFrom} max={dayStr(0)} on:change={() => loadKw(false)} /></label>
    <label class="dateby">engine <select bind:value={source} on:change={() => loadKw(false)}><option value="bing">Bing</option><option value="google">Google</option><option value="all">Bing + Google</option></select></label>
    <label class="dateby">orders by <select bind:value={dateBy} on:change={() => loadKw(false)}><option value="order">order date</option><option value="click">click date</option></select></label>
    <span class="presets">{#each presets as p}<button class:on={rangeFrom === p.from && rangeTo === dayStr(1)} on:click={() => setRange(p.from)}>{p.label}</button>{/each}</span>
    <span class="meta">{fmtDay(t.from)} – {fmtDay(t.to)}{t.partialCoverage ? ' · before 30 Aug: Shopify journey only' : ''}</span>
  </div>
  <div class="cards" class:stale={kwLoading}>
    <div class="card"><span class="label">Spend</span><span class="value">{money(t.spend)}</span><span class="sub">{t.clicks} clicks · {t.impressions.toLocaleString('en-GB')} impressions</span></div>
    <div class="card"><span class="label">Store orders <small>(attributed)</small></span><span class="value">{t.storeOrders}</span><span class="sub">{t.storeOrdersKeyed} keyed to a keyword{t.storeOrdersUnkeyed ? ` + ${t.storeOrdersUnkeyed} without` : ''}{#if source !== 'google'} · Bing counted {t.bingConversions}{/if}{#if source !== 'bing'} · Google counted {t.googleConversions ?? '—'}{/if}{#if source === 'all'} · {t.bySource.bing || 0} Bing / {t.bySource.google || 0} Google{/if}<br />{Object.entries(t.sources).map(([k, v]) => `${v} ${k}`).join(' · ') || '—'}</span></div>
    <div class="card"><span class="label">Revenue</span><span class="value">{money(t.revenue)}</span><span class="sub">from the orders themselves · ROAS {t.roas ?? '—'} · net {money(t.net)}</span></div>
    <div class="card"><span class="label">Cost per order</span><span class="value">{money(t.cpo)}</span><span class="sub">{money(t.storeSpend)} ÷ {t.storeOrders} attributed orders{t.storeOrdersUnkeyed ? ` · ${money(t.cpoKeyed)} on keyed only` : ''}</span></div>
    {#if kw.visibility}<div class="card"><span class="label">On the results page <small>(cold-visitor snapshots — read true visibility from impression share)</small></span><span class="value">{kw.visibility && kw.visibility.snapshots ? Math.round(kw.visibility.shown / kw.visibility.snapshots * 100) + '%' : '—'}</span><span class="sub">{kw.visibility && kw.visibility.snapshots ? `our ad in ${kw.visibility.shown} of ${kw.visibility.snapshots} latest snapshots · ${fmtTime(kw.visibility.latestAt)}` : 'no snapshots yet'}</span></div>{/if}
    {#if source !== 'bing'}<div class="card"><span class="label">Google data through</span><span class="value">{kw.googleExport ? fmtDay(kw.googleExport.to) : '—'}</span><span class="sub">{kw.googleExport ? `exported ${fmtTime(kw.googleExport.exportedAt)} · read ${fmtTime(kw.googleExport.readAt)}` : 'not connected'}{t.googleNonKeywordSpend ? ` · ${money(t.googleNonKeywordSpend)} of spend outside keywords` : ''}</span></div>{/if}
    {#if source !== 'google'}<div class="card"><span class="label">Data through</span><span class="value">{fmtDay(kw.lastDataDay)}</span><span class="sub">auction insights through {fmtDay(kw.auctionThrough)} · history from {fmtDay(kw.firstDataDay)}{#if kw.lastRun} · collector {kw.lastRun.ok ? 'ok' : '⚠️ FAILED'} {fmtTime(kw.lastRun.at)}{/if}</span></div>{/if}
  </div>
  <h4 class="charthead">Daily spend, orders and cost per order <small>bars = spend (left axis) with orders on top · line = 7-day trailing CPO (right axis) · hover a day</small></h4>
  <div class="chartbox">
    <svg viewBox={`0 0 ${CW} ${CH}`} preserveAspectRatio="none" class="dchart">
      {#each [0, 0.5, 1] as f}
        <line x1={CP.l} x2={CW - CP.r} y1={ySpend(f * spendMax / 1.35)} y2={ySpend(f * spendMax / 1.35)} class="grid" />
        <text x={CP.l - 6} y={ySpend(f * spendMax / 1.35) + 4} class="ylab">£{(f * spendMax / 1.35).toFixed(0)}</text>
        <text x={CW - CP.r + 6} y={yCpo(f * cpoMax) + 4} class="ylab r">£{(f * cpoMax).toFixed(0)}</text>
      {/each}
      {#each dTicks as t}<text x={cx(daily.indexOf(t))} y={CH - 8} class="xlab">{fmtDay(t.date)}</text>{/each}
      {#each msMarksList as m, mi}<line x1={m.x} x2={m.x} y1={CP.t} y2={CH - CP.b} class="msline" /><text x={m.x + 3} y={CP.t + 10 + (mi % 3) * 11} class="mslab">📌 {m.detail.length > 34 ? m.detail.slice(0, 33) + '…' : m.detail}</text>{/each}
      {#each daily as d, i}
        <rect x={cx(i) - slot * 0.36} y={ySpend(d.spend)} width={slot * 0.72} height={Math.max(0, CH - CP.b - ySpend(d.spend))} class="bar" class:noorder={d.spend > 0 && !d.orders}><title>{tip(d)}</title></rect>
        {#if d.orders}<text x={cx(i)} y={ySpend(d.spend) - 3} class="ocount">{d.orders}</text>{/if}
        {#if d.cpo != null}<circle cx={cx(i)} cy={yCpo(Math.min(d.cpo, cpoMax))} r="2" class="cpodot"><title>{tip(d)}</title></circle>{/if}
      {/each}
      <path d={cpoPath} class="cpoline" />
    </svg>
    <div class="legend"><span><i class="sw barsw"></i> spend</span><span><i class="sw barsw2"></i> spend, no orders that day</span><span><i class="sw cpolinesw"></i> 7-day CPO</span><span><i class="sw cpodotsw"></i> day CPO</span></div>
  </div>
  <h4 class="charthead">By weekday <small>per-day averages over the selected range · orders by {kw.totals.dateBy === 'order' ? 'order date' : 'click date'}</small></h4>
  <div class="tablewrap">
      <table class="wk">
        <thead><tr><th>day</th><th class="num">days</th><th class="num">impr/day</th><th class="num">clicks/day</th><th class="num">CPC</th><th class="num">impr share</th><th class="num">orders/day</th><th class="num">CVR</th><th class="num">CPO</th><th class="num">revenue/day</th><th class="num">ROAS</th></tr></thead>
        <tbody>
          {#each kw.weekday as r}
            <tr class:weekend={r.day === 'Sat' || r.day === 'Sun'}>
              <td>{r.day}</td><td class="num">{r.days}</td><td class="num">{r.impressionsPerDay ?? '—'}</td><td class="num">{r.clicksPerDay ?? '—'}</td><td class="num">{r.cpc != null ? '£' + r.cpc.toFixed(2) : '—'}</td><td class="num">{r.impressionShare != null ? r.impressionShare + '%' : '—'}</td>
              <td class="num" class:good={r.ordersPerDay > 0}>{r.ordersPerDay ?? '—'}</td><td class="num">{r.cvr != null ? r.cvr + '%' : '—'}</td><td class="num" class:bad={r.cpo != null && r.cpo > 8} class:good={r.cpo != null && r.cpo < 5}>{r.cpo != null ? '£' + r.cpo.toFixed(2) : '—'}</td><td class="num">{r.revenuePerDay != null ? '£' + r.revenuePerDay.toFixed(2) : '—'}</td><td class="num">{r.roas ?? '—'}</td>
            </tr>
          {/each}
        </tbody>
      </table>
  </div>
  {#if kw.geo && kw.geo.days}
  <h4 class="charthead">By geography <small>Bing's geographic report over the selected range ({kw.geo.days} days) · store orders placed by billing postcode area ({kw.geo.storeLocated} of {kw.geo.storeTotal} located)</small></h4>
  <div class="two">
    <div>
      <table class="wk">
        <thead><tr><th>region</th><th class="num">£/day</th><th class="num">share</th><th class="num">CTR</th><th class="num">CPC</th><th class="num">store orders</th><th class="num">store CPO</th><th class="num">Bing conv</th><th class="num">£/Bing conv</th></tr></thead>
        <tbody>{#each kw.geo.regions.slice(0, 8) as r}<tr><td>{r.key}</td><td class="num">£{r.perDay}</td><td class="num">{Math.round((r.spendShare || 0) * 100)}%</td><td class="num">{r.ctr ?? '—'}%</td><td class="num">£{r.cpc ?? '—'}</td><td class="num" class:good={r.storeOrders > 0}>{r.storeOrders}</td><td class="num" class:bad={r.storeCpo > 10} class:good={r.storeCpo != null && r.storeCpo < 6}>{r.storeCpo != null ? '£' + r.storeCpo : '—'}</td><td class="num">{r.bingConversions}</td><td class="num">{r.costPerBingConv != null ? '£' + r.costPerBingConv : '—'}</td></tr>{/each}</tbody>
      </table>
      <table class="wk" style="margin-top:.75rem">
        <thead><tr><th>London vs rest × weekday</th><th class="num">£/day</th><th class="num">CTR</th><th class="num">CPC</th><th class="num">store orders</th><th class="num">store CPO</th><th class="num">Bing conv</th><th class="num">£/Bing conv</th></tr></thead>
        <tbody>{#each kw.geo.londonSplit as r}<tr class:weekend={r.key.endsWith('weekend')}><td>{r.key}</td><td class="num">£{r.perDay ?? '—'}</td><td class="num">{r.ctr ?? '—'}%</td><td class="num">£{r.cpc ?? '—'}</td><td class="num" class:good={r.storeOrders > 0}>{r.storeOrders}</td><td class="num" class:bad={r.storeCpo > 10} class:good={r.storeCpo != null && r.storeCpo < 6}>{r.storeCpo != null ? '£' + r.storeCpo : '—'}</td><td class="num">{r.bingConversions}</td><td class="num">{r.costPerBingConv != null ? '£' + r.costPerBingConv : '—'}</td></tr>{/each}</tbody>
      </table>
    </div>
    <div>
      <table class="wk">
        <thead><tr><th>metro area (top 20 by spend)</th><th class="num">£/day</th><th class="num">share</th><th class="num">CPC</th><th class="num">store orders</th><th class="num">store CPO</th><th class="num">Bing conv</th><th class="num">£/Bing conv</th></tr></thead>
        <tbody>{#each kw.geo.metros as r}<tr><td>{r.key}</td><td class="num">£{r.perDay}</td><td class="num">{Math.round((r.spendShare || 0) * 100)}%</td><td class="num">£{r.cpc ?? '—'}</td><td class="num" class:good={r.storeOrders > 0}>{r.storeOrders}</td><td class="num" class:bad={r.storeCpo > 10} class:good={r.storeCpo != null && r.storeCpo < 6}>{r.storeCpo != null ? '£' + r.storeCpo : '—'}</td><td class="num">{r.bingConversions}</td><td class="num">{r.costPerBingConv != null ? '£' + r.costPerBingConv : '—'}</td></tr>{/each}</tbody>
      </table>
    </div>
  </div>
  {/if}
  <div class="kwbar">
    <label><input type="checkbox" bind:checked={showInactive} /> show paused &amp; deleted</label>
    <span class="meta">{kwRows.length} keywords · computed {fmtTime(kw.computedAt)}{kw.cached ? ' (cached)' : ''}</span>
    <button class="refresh" on:click={() => loadKw(true)} disabled={kwLoading}>Refresh</button>
  </div>
  <div class="tablewrap">
  <table class="kw">
    <thead><tr>
      <th class="sortable" on:click={() => sortBy('text')}>keyword</th><th>match</th>
      <th class="sortable num" on:click={() => sortBy('bid')}>our bid</th>
      <th class="sortable num" on:click={() => sortBy('firstPage')}>1st page</th>
      <th class="sortable num" on:click={() => sortBy('mainline')}>mainline</th>
      <th class="sortable num" on:click={() => sortBy('qs')}>QS</th>
      <th class="sortable num" on:click={() => sortBy('is')}>impr share</th>
      <th class="sortable num" on:click={() => sortBy('spend')}>spend</th>
      <th class="sortable num" on:click={() => sortBy('clicks')}>clicks</th>
      <th class="sortable num" on:click={() => sortBy('cpc')}>CPC</th>
      <th class="sortable num" on:click={() => sortBy('orders')}>store orders{kw.totals.clipped ? '†' : ''}</th>
      <th class="sortable num" on:click={() => sortBy('cvr')}>CVR</th>
      <th class="sortable num" on:click={() => sortBy('cpo')}>CPO{kw.totals.clipped ? '†' : ''}</th>
      <th class="sortable num" on:click={() => sortBy('revenue')}>revenue</th>
      <th class="sortable num" on:click={() => sortBy('roas')}>ROAS</th>
      <th class="sortable num" on:click={() => sortBy('net')}>net</th>
      <th class="sortable num" on:click={() => sortBy('bing')} title="what the ad engine's own consent-gated tag counted">{source === 'bing' ? 'Bing' : source === 'google' ? 'Google' : 'engine'} conv{kw.totals.clipped ? '†' : ''}</th>
      <th class="sortable" on:click={() => sortBy('comp')}>competitors</th>
    </tr></thead>
    <tbody>
      {#each kwRows as k (k.id)}
        <tr class:muted={!k.present || k.status !== 'Active' || k.adGroupStatus !== 'Active'}>
          <td>{#if k.source === 'google'}{k.text}{#if source === 'all'}<span class="chip">Google</span>{/if}{:else}<a href={`#/analytics/keywords/${k.id}${kwLink}`}>{k.text}</a>{#if source === 'all'}<span class="chip">Bing</span>{/if}{/if}{#if !k.present}<span class="chip off">deleted</span>{:else if k.status !== 'Active'}<span class="chip off">{k.status.toLowerCase()}</span>{:else if k.adGroupStatus !== 'Active'}<span class="chip off">group paused</span>{/if}<br /><small class="grp">{k.adGroupName || ''}</small></td>
          <td>{k.matchType}</td>
          <td class="num" class:bad={k.belowFirstPage} title={k.bidSource === 'adgroup' ? 'ad-group default bid' : ''}>{money(k.bid)}{k.bidSource === 'adgroup' ? '*' : ''}</td>
          <td class="num">{money(k.latest.firstPageBid)}<small class="dl">{delta(k.firstPageBidDelta)}</small></td>
          <td class="num">{money(k.latest.mainlineBid)}</td>
          <td class="num">{k.latest.qualityScore ?? '—'}</td>
          <td class="num">{pc(k.latest.impressionShare)}</td>
          <td class="num">{money(k.r.spend)}</td>
          <td class="num">{k.r.clicks}</td>
          <td class="num">{money(k.r.cpc)}</td>
          <td class="num" class:good={k.r.storeOrders > 0}>{k.r.storeOrders}</td>
          <td class="num">{k.r.cvr != null ? k.r.cvr + '%' : '—'}</td>
          <td class="num">{money(k.r.cpo)}</td>
          <td class="num">{money(k.r.revenue)}</td>
          <td class="num">{k.r.roas ?? '—'}</td>
          <td class="num" class:bad={k.r.net < -0.005} class:good={k.r.net > 0.005}>{money(k.r.net)}</td>
          <td class="num" class:warn={k.r.storeOrders > k.r.engineConversions}>{k.r.engineConversions}</td>
          <td title={k.competitors.map((c) => `${c.domain} ${Math.round(c.impressionShare * 100)}%`).join(', ')}>{k.competitorCount || '—'}{#if k.competitors[0]}&nbsp;<small class="grp">{k.competitors[0].domain}</small>{/if}</td>
        </tr>
      {/each}
    </tbody>
  </table>
  </div>
  <p class="sub-note">Store orders come from the store's own attribution (cookieless URL tag, consented cookies, then Shopify's customer journey for returning visitors), dated by {kw.totals.dateBy === 'order' ? 'order date' : 'the ad click, so they sit with the spend that bought them'}. CPO = {ENGINE[source]} spend ÷ that engine's store orders over the same days. "Conv" is what the engine's own consent-gated tag counted over those same days — the gap is the under-count. {#if source !== 'bing'}Google keywords have no detail page yet, and Google has no bid estimates or auction insights, so those columns stay empty. {/if} Revenue is the order total from Shopify; ROAS = revenue ÷ spend; net = revenue − spend. {#if kw.totals.partialCoverage}Coverage note: our own tags (cookie + cookieless) went live 30 Aug; before that only Shopify's consent-gated journey record exists, which historically caught about half of orders — so orders, revenue, CPO and ROAS for earlier days understate the truth. {/if}* = ad-group default bid. 1st-page delta = this week vs last. Bid, estimates, QS and share are current values, not range averages.</p>

  <h3 id="queries">Search queries <small>what we actually buy · Bing's query report joined to store orders by query · same range as above</small></h3>
  {#if qLoading && !qd}
    <p>Loading search queries…</p>
  {:else if qError}
    <p class="error">{qError}</p>
  {:else if qd}
    {@const qt = qd.totals}
    <div class="cards">
      <div class="card"><span class="label">Queries bought</span><span class="value">{qt.queries}</span><span class="sub">{qt.clicks} clicks · {money(qt.spend)} · {qt.singleClickQueries} one-click queries</span></div>
      <div class="card"><span class="label">Clicks on a query that is one of our keywords</span><span class="value">{qt.clicks ? Math.round(qt.ownKeywordClicks / qt.clicks * 100) : 0}%</span><span class="sub">the rest are variants Bing matched for us</span></div>
      <div class="card"><span class="label">Clicks where two of our keywords served the same query</span><span class="value">{qt.clicks ? Math.round(qt.multiClicks / qt.clicks * 100) : 0}%</span><span class="sub">internal overlap · Bing picks one per auction</span></div>
      <div class="card"><span class="label">Clicks on queries first seen in this range</span><span class="value">{qt.clicks ? Math.round(qt.newClicks / qt.clicks * 100) : 0}%</span><span class="sub">a jump here = matching widened · see match_drift events</span></div>
      <div class="card"><span class="label">Store orders with a query</span><span class="value">{qt.ordersWithQuery}<small style="font-size:.9rem;font-weight:400"> of {qt.ordersInRange}</small></span><span class="sub">query carried by the cookieless tag / cookie since 30 Aug · Bing counted {qt.bingConversions}</span></div>
      <div class="card"><span class="label">Suggested</span><span class="value">{qt.bySuggestion.promote.queries}<small style="font-size:.9rem;font-weight:400"> promote · {qt.bySuggestion.negative.queries} negative</small></span><span class="sub">promote = converting without its own keyword ({qt.bySuggestion.promote.storeOrders} orders, {money(qt.bySuggestion.promote.spend)}) · negative = off-theme ({qt.bySuggestion.negative.clicks} clicks, {money(qt.bySuggestion.negative.spend)})</span></div>
    </div>
    <div class="kwbar">
      <span class="presets">
        {#each [['all', 'all'], ['promote', 'promote'], ['negative', 'negative?'], ['review', 'review'], ['orders', 'with orders'], ['nokw', 'no keyword of its own'], ['multi', 'served by 2+ keywords'], ['new', 'new in range'], ['keyword', 'is a keyword']] as [k, l]}
          <button class:on={qFilter === k} on:click={() => { qFilter = k; qLimit = 60; }}>{l}</button>
        {/each}
      </span>
      <input class="qsearch" type="search" placeholder="find a query…" bind:value={qSearch} />
      <span class="meta">{qRows.length} queries{qRows.length > qLimit ? ` · showing ${qLimit}` : ''} · computed {fmtTime(qd.computedAt)}{qd.cached ? ' (cached)' : ''}</span>
    </div>
    <div class="tablewrap">
    <table class="kw">
      <thead><tr>
        <th class="sortable" on:click={() => qSortBy('query')}>query</th>
        <th class="sortable num" on:click={() => qSortBy('impressions')}>impr</th>
        <th class="sortable num" on:click={() => qSortBy('clicks')}>clicks</th>
        <th class="sortable num" on:click={() => qSortBy('ctr')}>CTR</th>
        <th class="sortable num" on:click={() => qSortBy('spend')}>spend</th>
        <th class="sortable num" on:click={() => qSortBy('cpc')}>CPC</th>
        <th class="sortable num" on:click={() => qSortBy('bingConversions')}>Bing conv</th>
        <th class="sortable num" on:click={() => qSortBy('storeOrders')}>store orders</th>
        <th class="sortable num" on:click={() => qSortBy('cpo')}>CPO</th>
        <th class="sortable num" on:click={() => qSortBy('revenue')}>revenue</th>
        <th class="sortable" on:click={() => qSortBy('served')}>served by</th>
        <th class="sortable" on:click={() => qSortBy('firstSeen')}>first seen</th>
        <th>action</th>
      </tr></thead>
      <tbody>
        {#each qRows.slice(0, qLimit) as r (r.query)}
          <tr class:muted={r.unreported}>
            <td class="qtext">{r.query}{#if r.unreported}<span class="chip off" title="an order carried this query but Bing's query report never listed it">not in report</span>{/if}</td>
            <td class="num">{r.impressions || '—'}</td>
            <td class="num">{r.clicks || '—'}</td>
            <td class="num">{r.ctr != null ? r.ctr + '%' : '—'}</td>
            <td class="num">{r.spend ? money(r.spend) : '—'}</td>
            <td class="num" class:warn={r.expensive}>{money(r.cpc)}</td>
            <td class="num">{r.bingConversions || '—'}</td>
            <td class="num" class:good={r.storeOrders > 0} title={r.orderNames.join(', ')}>{r.storeOrders || '—'}</td>
            <td class="num" class:bad={r.cpo != null && r.cpo > 10} class:good={r.cpo != null && r.cpo < 6}>{money(r.cpo)}</td>
            <td class="num">{r.revenue ? money(r.revenue) : '—'}</td>
            <td class="served">{#each r.via as v, i}{#if i}<span class="grp"> · </span>{/if}<a href={`#/analytics/keywords/${v.id}${kwLink}`} class:strong={String(v.id) === r.ownKeywordId}>{v.text}</a><small class="grp"> {v.matchType === 'Phrase' ? 'ph' : v.matchType === 'Exact' ? 'ex' : 'br'} {v.clicks}</small>{/each}{#if !r.via.length}<span class="grp">—</span>{/if}</td>
            <td><small class="grp">{fmtDay(r.firstSeen)}{r.newInRange ? ' · new' : ''}</small></td>
            <td>{#if r.suggestion !== 'ok'}<span class={`chip sug ${r.suggestion}`} title={r.why}>{sugLabel[r.suggestion]}</span>{/if}</td>
          </tr>
        {/each}
      </tbody>
    </table>
    </div>
    {#if qRows.length > qLimit}<p class="sub-note"><button class="refresh" on:click={() => (qLimit += 100)}>show {Math.min(100, qRows.length - qLimit)} more</button></p>{/if}
    <p class="sub-note">Rows are search queries from Bing's query report; "served by" lists every keyword of ours that matched it (match type + clicks), bold when the query is literally one of our keywords. Store orders join by the query the buyer's click carried (cookieless tag / cookie, from 30 Aug), so pre-30-Aug orders have no query and don't appear here. <b>promote</b> = converts but has no keyword of its own, so it can't be bid or read separately; <b>negative?</b> = off-theme word (free, template, group, leaving, competitor brands…) worth a campaign negative; <b>review</b> = 10+ clicks and no orders from either count. Match types no longer bound what a keyword serves on, so this table, not the keyword list, is what the account is buying.</p>

    <h4 class="charthead">Coverage <small>Bing Keyword Planner ideas (UK, Bing-owned sites, 12 months{qd.ideas ? `, refreshed ${fmtDay(qd.ideas.fetchedAt.slice(0, 10))}` : ''}) vs what we have and what we've ever bought</small></h4>
    <div class="kwbar">
      <label>status <select bind:value={covStatus}><option value="unseen">never seen</option><option value="seen">seen, no clicks</option><option value="bought">bought</option><option value="keyword">our keyword</option><option value="all">all</option></select></label>
      <label>min searches/month <input type="number" bind:value={covMin} min="0" step="50" style="width:5em" /></label>
      <label><input type="checkbox" bind:checked={covIntent} /> ecard intent only</label>
      <label>sort <select bind:value={covSort}><option value="avg">by average month</option><option value="peak">by peak month</option><option value="season">by seasonality</option></select></label>
      <span class="meta">{covRows.length} ideas{covRows.length > covLimit ? ` · showing ${covLimit}` : ''} · {qd.coverage.length} total</span>
    </div>
    <div class="tablewrap">
    <table class="kw">
      <thead><tr><th>keyword idea</th><th class="num">avg/month</th><th class="num">peak</th><th class="num">× avg</th><th class="num">suggested bid</th><th>competition</th><th>status</th><th class="num">our clicks ever</th><th>first seen</th></tr></thead>
      <tbody>
        {#each covRows.slice(0, covLimit) as c (c.keyword)}
          <tr>
            <td class="qtext">{c.keyword}</td>
            <td class="num">{c.avgMonthly.toLocaleString('en-GB')}</td>
            <td class="num" title={c.months.map((m) => `${m.month}: ${m.searches}`).join('\n')}>{c.peakSearches.toLocaleString('en-GB')} <small class="grp">{c.peakLabel}</small></td>
            <td class="num" class:warn={c.seasonality >= 3}>{c.seasonality ?? '—'}</td>
            <td class="num">{c.suggestedBid ? money(c.suggestedBid) : '—'}</td>
            <td>{c.competition || '—'}</td>
            <td><span class={`chip cov ${c.status}`}>{covLabel[c.status]}</span>{#if c.ownKeywordId} <a href={`#/analytics/keywords/${c.ownKeywordId}${kwLink}`}><small class="grp">open</small></a>{/if}</td>
            <td class="num">{c.boughtClicks || '—'}</td>
            <td><small class="grp">{fmtDay(c.firstSeen)}</small></td>
          </tr>
        {/each}
      </tbody>
    </table>
    </div>
    {#if covRows.length > covLimit}<p class="sub-note"><button class="refresh" on:click={() => (covLimit += 100)}>show {Math.min(100, covRows.length - covLimit)} more</button></p>{/if}
    <p class="sub-note">Volumes are Bing's planner estimates for the UK on Bing-owned sites (where ~75% of our impressions come from); hover a peak for the 12-month series. "Bought" = the exact query text has taken clicks under some keyword of ours; "seen" = impressions only; "never seen" = demand we've never been served for. Seasonality = peak month ÷ average month; anything ≥3 is an occasion to plan a campaign around (Christmas, Halloween, New Year, Valentine's). Ideas refresh monthly from the collector.</p>
  {/if}

  <div class="two">
    <div>
      <h3>Milestones <small>manual annotations · drawn on every keyword timeline</small></h3>
      <div class="milestones">
        {#each kw.milestones as m}
          <div class="ms"><span class="d">{fmtDay(m.date)}</span> <span class="pin">📌</span> {m.detail} <button class="x" title="remove" on:click={() => removeMilestone(m)}>×</button></div>
        {/each}
        <form class="msform" on:submit|preventDefault={addMilestone}>
          <input type="date" bind:value={msDate} max={dayStr(0)} min="2026-06-01" required />
          <input type="text" bind:value={msLabel} placeholder="e.g. Say Thanks reply live in viewer" maxlength="140" required />
          <button type="submit" disabled={msBusy}>Add</button>
        </form>
        {#if msError}<p class="error">{msError}</p>{/if}
      </div>

      <h3>Event log <small>last 30 days</small></h3>
      {#if !kw.events.length}<p class="empty">Nothing yet.</p>{:else}
      <ul class="events">
        {#each kw.events.slice(0, 80) as e}
          <li><span class="d">{e.date}</span> <span style={`color:${sevColor[e.severity] || '#666'}`}>●</span> {#if e.keywordId}<a href={`#/analytics/keywords/${e.keywordId}${kwLink}`}>{e.keyword}</a>{/if} <span>{e.detail}</span></li>
        {/each}
      </ul>
      {/if}
    </div>
    <div>
      <h3>Competitor domains <small>auction insights (latest week with data) + page-one snapshots (last 7 days, {kw.visibility.snapshots} pages)</small></h3>
      {#if !kw.domains.length}<p class="empty">No auction data yet.</p>{:else}
      <table>
        <thead><tr><th>domain</th><th class="num">keywords</th><th class="num">avg share</th><th class="num" title="share of the last 7 days' page-one snapshots this domain appeared in">on page 7d</th><th class="num" title="share of snapshots in the mainline (above results)">mainline</th><th class="num" title="average mainline slot when present">avg slot</th><th>strongest on</th><th>latest ad seen</th></tr></thead>
        <tbody>
          {#each kw.domains.slice(0, 30) as d}
            <tr><td>{d.domain}</td><td class="num">{d.keywords.length || '—'}</td><td class="num">{d.keywords.length ? Math.round(d.avgShare * 100) + '%' : '—'}</td>
              <td class="num">{d.snap ? Math.round(d.snap.presenceRate * 100) + '%' : '—'}</td><td class="num">{d.snap ? Math.round(d.snap.mainlineRate * 100) + '%' : '—'}</td><td class="num">{d.snap && d.snap.avgSlot != null ? d.snap.avgSlot : '—'}</td>
              <td>{#each d.keywords.slice(0, 3) as x}<a href={`#/analytics/keywords/${x.id}${kwLink}`}>{x.text}</a> <small class="grp">{Math.round(x.impressionShare * 100)}%</small>{' '}{/each}{#if !d.keywords.length && d.seenOnPage}<small class="grp">on page for: {d.seenOnPage.join(', ')}</small>{/if}</td>
              <td class="creative">{#if d.creative}<span title={`${d.creative.query} · ${d.creative.device} · ${d.creative.block} ${d.creative.slot} · ${fmtTime(d.creative.at)}`}>{d.creative.headline}</span>{#if d.creative.description}<br /><small class="grp">{d.creative.description}</small>{/if}{:else}<small class="grp">—</small>{/if}</td></tr>
          {/each}
        </tbody>
      </table>
      {/if}
    </div>
  </div>
{/if}

<style>
  .head { display: flex; align-items: baseline; gap: 1rem; }
  h2 { margin: .5rem 0 1rem; }
  h3 { margin: 1.75rem 0 .5rem; }
  h3 small { font-weight: 400; color: #888; }
  .meta { font-size: .8rem; color: #888; }
  .refresh { cursor: pointer; border: 1px solid #ccc; background: #fff; padding: .25rem .7rem; border-radius: 6px; font-size: .8rem; }
  .refresh:disabled { opacity: .5; cursor: default; }
  .error { color: #b00020; }
  .note { background: #fff7e6; border: 1px solid #f0dcae; border-radius: 8px; padding: .6rem .9rem; font-size: .85rem; }
  .note code { background: #f3ead3; padding: .05rem .3rem; border-radius: 4px; }

  .cards { display: grid; grid-template-columns: repeat(auto-fit, minmax(190px, 1fr)); gap: .75rem; margin: 1rem 0; transition: opacity .15s; }
  .cards.stale { opacity: .45; }
  .updating { margin-left: .6rem; font-size: .8rem; font-weight: 400; color: #2a5db0; }
  .card { background: #fff; border: 1px solid #e5e5e5; border-radius: 10px; padding: .8rem 1rem; display: flex; flex-direction: column; gap: .2rem; }
  .card .label { font-size: .78rem; color: #777; text-transform: uppercase; letter-spacing: .03em; }
  .card .label small { text-transform: none; letter-spacing: 0; }
  .card .value { font-size: 1.6rem; font-weight: 700; }
  .card .sub { font-size: .78rem; color: #888; }

  table { width: 100%; border-collapse: collapse; background: #fff; border: 1px solid #e5e5e5; border-radius: 10px; overflow: hidden; font-size: .88rem; }
  th, td { text-align: left; padding: .5rem .75rem; border-bottom: 1px solid #eee; }
  th { background: #f7f7f7; font-size: .78rem; text-transform: uppercase; letter-spacing: .03em; color: #666; }
  tbody tr:last-child td { border-bottom: 0; }

  .sub-note { font-size: .85rem; color: #666; margin: .5rem 0 0; }
  .empty { color: #888; font-style: italic; }

  .chart { display: flex; align-items: flex-end; gap: 2px; height: 110px; background: #fff; border: 1px solid #e5e5e5; border-radius: 10px; padding: .6rem; }
  .bar-slot { flex: 1; height: 100%; display: flex; align-items: flex-end; }
  .bar { width: 100%; background: #4a7dbd; border-radius: 2px 2px 0 0; min-height: 2px; }

  .badge { font-size: .72rem; padding: .15rem .5rem; border-radius: 999px; white-space: nowrap; }
  .badge.ok { background: #e2f5e5; color: #1c7a2e; }
  .badge.info { background: #e8f0fe; color: #2a5db0; }

  .rangebar { display: flex; align-items: center; gap: .75rem; flex-wrap: wrap; font-size: .82rem; margin: .5rem 0; }
  .rangebar input[type=date] { font: inherit; padding: .2rem .4rem; border: 1px solid #ccc; border-radius: 6px; margin-left: .25rem; }
  .dateby select { font: inherit; font-size: .8rem; padding: .15rem .3rem; border: 1px solid #ccc; border-radius: 6px; }
  .presets { display: flex; gap: .25rem; flex-wrap: wrap; }
  .presets button { cursor: pointer; border: 1px solid #ccc; background: #fff; padding: .2rem .55rem; border-radius: 999px; font-size: .76rem; }
  .presets button.on { background: #2a5db0; color: #fff; border-color: #2a5db0; }
  .rangebar .meta { margin-left: auto; }
  .milestones { background: #fff; border: 1px solid #e5e5e5; border-radius: 10px; padding: .4rem .6rem; font-size: .82rem; }
  .ms { padding: .25rem 0; border-bottom: 1px solid #eee; display: flex; gap: .3rem; align-items: center; }
  .ms .d { color: #888; font-variant-numeric: tabular-nums; }
  .ms .x { margin-left: auto; border: 0; background: none; color: #bbb; cursor: pointer; font-size: 1rem; }
  .ms .x:hover { color: #b00020; }
  .msform { display: flex; gap: .4rem; padding-top: .4rem; }
  .msform input[type=text] { flex: 1; font: inherit; padding: .25rem .5rem; border: 1px solid #ccc; border-radius: 6px; }
  .msform input[type=date] { font: inherit; padding: .2rem .4rem; border: 1px solid #ccc; border-radius: 6px; }
  .msform button { cursor: pointer; border: 1px solid #2a5db0; background: #2a5db0; color: #fff; padding: .25rem .7rem; border-radius: 6px; font-size: .8rem; }
  .charthead { margin: 1rem 0 .4rem; font-size: .95rem; }
  .charthead small { font-weight: 400; color: #888; font-size: .75rem; }
  .chartbox { background: #fff; border: 1px solid #e5e5e5; border-radius: 10px; padding: .6rem; }
  .dchart { width: 100%; height: 220px; display: block; }
  .grid { stroke: #eee; stroke-width: 1; }
  .ylab { font-size: 10px; fill: #888; text-anchor: end; }
  .ylab.r { text-anchor: start; fill: #7a3fbd; }
  .xlab { font-size: 10px; fill: #888; text-anchor: middle; }
  .bar { fill: #4a7dbd; opacity: .55; }
  .bar.noorder { fill: #b9cbe3; opacity: .7; }
  .ocount { font-size: 10px; fill: #1c7a2e; text-anchor: middle; font-weight: 700; }
  .cpodot { fill: #7a3fbd; opacity: .6; }
  .cpoline { fill: none; stroke: #7a3fbd; stroke-width: 2; vector-effect: non-scaling-stroke; }
  .msline { stroke: #b26a00; stroke-width: 1.2; stroke-dasharray: 5 3; vector-effect: non-scaling-stroke; }
  .mslab { font-size: 10px; fill: #b26a00; }
  .legend { display: flex; flex-wrap: wrap; gap: 1rem; font-size: .75rem; color: #666; padding: .4rem .2rem 0; }
  .sw { display: inline-block; width: 14px; height: 3px; vertical-align: middle; margin-right: .3rem; }
  .sw.barsw { background: #4a7dbd; height: 10px; } .sw.barsw2 { background: #b9cbe3; height: 10px; }
  .sw.cpolinesw { background: #7a3fbd; } .sw.cpodotsw { background: #7a3fbd; width: 6px; height: 6px; border-radius: 50%; opacity: .6; }
  td.creative { max-width: 360px; white-space: normal; font-size: .78rem; }
  table.wk { font-size: .8rem; }
  table.wk td, table.wk th { padding: .3rem .5rem; white-space: nowrap; }
  table.wk tr.weekend td { background: #fafafa; }
  table.wk td.num { text-align: right; font-variant-numeric: tabular-nums; }
  table.wk td.good { color: #1c7a2e; font-weight: 600; }
  table.wk td.bad { color: #b00020; }
  .kwbar { display: flex; align-items: center; gap: 1rem; margin: .25rem 0 .5rem; font-size: .82rem; }
  .kwbar .meta { margin-left: auto; }
  .tablewrap { overflow-x: auto; }
  table.kw { font-size: .82rem; }
  table.kw td, table.kw th { padding: .35rem .5rem; white-space: nowrap; }
  th.sortable { cursor: pointer; user-select: none; }
  th.sortable:hover { color: #222; }
  .num { text-align: right; font-variant-numeric: tabular-nums; }
  td.bad { color: #b00020; font-weight: 600; }
  td.good { color: #1c7a2e; font-weight: 600; }
  td.warn { color: #b26a00; }
  tr.muted td { color: #999; }
  .chip { font-size: .66rem; padding: .05rem .4rem; border-radius: 999px; background: #f0f0f0; color: #888; margin-left: .4rem; vertical-align: middle; }
  .grp { color: #999; font-size: .72rem; }
  .dl { color: #888; font-size: .7rem; }
  .two { display: grid; grid-template-columns: 1fr 1fr; gap: 1.5rem; }
  @media (max-width: 1000px) { .two { grid-template-columns: 1fr; } }
  .events { list-style: none; padding: 0; margin: 0; font-size: .82rem; background: #fff; border: 1px solid #e5e5e5; border-radius: 10px; }
  .events li { padding: .3rem .6rem; border-bottom: 1px solid #eee; }
  .events li:last-child { border-bottom: 0; }
  .events .d { color: #888; font-variant-numeric: tabular-nums; margin-right: .3rem; }
  .events a { font-weight: 600; text-decoration: none; color: #2a5db0; }
  .qsearch { font: inherit; font-size: .8rem; padding: .25rem .5rem; border: 1px solid #ccc; border-radius: 6px; min-width: 14em; }
  td.qtext { white-space: normal; max-width: 320px; }
  td.served { white-space: normal; max-width: 360px; font-size: .78rem; }
  td.served a { text-decoration: none; color: #2a5db0; }
  td.served a.strong { font-weight: 700; }
  .chip.sug { margin-left: 0; font-size: .7rem; }
  .chip.sug.promote { background: #e2f5e5; color: #1c7a2e; }
  .chip.sug.negative { background: #fde8e8; color: #b00020; }
  .chip.sug.review { background: #fff3e0; color: #b26a00; }
  .chip.sug.keyword { background: #e8f0fe; color: #2a5db0; }
  .chip.cov { margin-left: 0; font-size: .7rem; }
  .chip.cov.keyword { background: #e8f0fe; color: #2a5db0; }
  .chip.cov.bought { background: #e2f5e5; color: #1c7a2e; }
  .chip.cov.seen { background: #f3f3f3; color: #666; }
  .chip.cov.unseen { background: #fff3e0; color: #b26a00; }
  .kwbar label select, .kwbar label input[type=number] { font: inherit; font-size: .8rem; padding: .15rem .3rem; border: 1px solid #ccc; border-radius: 6px; margin-left: .25rem; }
</style>
